import React, { useState, useEffect } from 'react';
import Loader from 'react-loader';
import styles from './Plan.module.scss';

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getFirstWeekdayOffset(year, month) {
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function buildCalendarCells(year, month) {
  const offset = getFirstWeekdayOffset(year, month);
  const days = getDaysInMonth(year, month);
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function dateString(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function pluralEvents(n) {
  if (n === 1) return '1 мероприятие';
  if (n >= 2 && n <= 4) return `${n} мероприятия`;
  return `${n} мероприятий`;
}

const Plan = ({ calendarData, loading, getEventsCalendar }) => {
  const today = new Date();
  const todayStr = dateString(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(today.getFullYear());

  useEffect(() => {
    getEventsCalendar(year, month);
  }, [year, month]);

  const eventsMap = {};
  if (calendarData && Array.isArray(calendarData.dates)) {
    calendarData.dates.forEach(({ date, items }) => {
      eventsMap[date] = items;
    });
  }

  const goToPrevMonth = () => {
    const newMonth = month === 1 ? 12 : month - 1;
    const newYear = month === 1 ? year - 1 : year;
    setYear(newYear);
    setMonth(newMonth);
    setSelectedDate(dateString(newYear, newMonth, 1));
  };

  const goToNextMonth = () => {
    const newMonth = month === 12 ? 1 : month + 1;
    const newYear = month === 12 ? year + 1 : year;
    setYear(newYear);
    setMonth(newMonth);
    setSelectedDate(dateString(newYear, newMonth, 1));
  };

  const handlePickMonth = (m) => {
    setYear(pickerYear);
    setMonth(m);
    setSelectedDate(dateString(pickerYear, m, 1));
    setShowPicker(false);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(dateString(year, month, day));
  };

  const openPicker = () => {
    setPickerYear(year);
    setShowPicker(true);
  };

  const cells = buildCalendarCells(year, month);
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }

  const selectedEvents = eventsMap[selectedDate] || [];

  const selectedDateLabel = (() => {
    const [y, m, d] = selectedDate.split('-');
    return `${parseInt(d)} ${MONTHS_RU[parseInt(m) - 1].toLowerCase()} ${y}`;
  })();

  return (
    <div className={styles.wrapper}>
      <p className={styles.title}>План мероприятий</p>

      {/* Navigation header */}
      <div className={styles.header}>
        <button className={styles.navBtn} onClick={goToPrevMonth} title="Предыдущий месяц">&#8592;</button>
        <span className={styles.monthLabel} onClick={openPicker}>
          {MONTHS_RU[month - 1]} {year}
        </span>
        <button className={styles.navBtn} onClick={goToNextMonth} title="Следующий месяц">&#8594;</button>
      </div>

      {/* Calendar grid */}
      <Loader loaded={!loading} color="#7d2e2d">
        <div className={styles.calendarWrap}>
          <table className={styles.calendarTable}>
            <thead>
              <tr>
                {WEEKDAYS.map((wd, i) => (
                  <th key={wd} className={i >= 5 ? styles.thWeekend : ''}>{wd}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((day, ci) => {
                    const isWeekend = ci >= 5;
                    if (!day) {
                      return <td key={ci} className={isWeekend ? styles.tdWeekend : ''}></td>;
                    }
                    const ds = dateString(year, month, day);
                    const count = eventsMap[ds] ? eventsMap[ds].length : 0;
                    const isSelected = ds === selectedDate;
                    const isToday = ds === todayStr;
                    const hasEvents = count > 0;
                    return (
                      <td key={ci} className={isWeekend ? styles.tdWeekend : ''}>
                        <span
                          className={[
                            styles.dayCell,
                            isSelected ? styles.dayCellSelected : '',
                            isToday && !isSelected ? styles.dayCellToday : '',
                          ].join(' ')}
                          onClick={() => handleDayClick(day)}
                        >
                          <span className={[
                            styles.dayNumber,
                            hasEvents && !isSelected ? styles.dayNumberBold : '',
                          ].join(' ')}>
                            {day}
                          </span>
                          {hasEvents && (
                            <span className={styles.eventCount}>{pluralEvents(count)}</span>
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Events list */}
        <div className={styles.eventsSection}>
          <div className={styles.eventsSectionTitle}>
            Мероприятия на {selectedDateLabel}
          </div>
          {selectedEvents.length === 0 ? (
            <div className={styles.emptyState}>На выбранную дату мероприятий нет</div>
          ) : (
            selectedEvents.map(event => (
              <div key={event.nid} className={styles.eventCard}>
                <div className={styles.eventAccent} />
                <div className={styles.eventBody}>
                  <div className={styles.eventTitle}>{event.title}</div>
                  <div className={styles.eventMeta}>
                    {event.time_text && <span className={styles.eventTime}>{event.time_text}</span>}
                    {event.place && <span className={styles.eventPlace}>{event.place}</span>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Loader>

      {/* Month/Year picker modal */}
      {showPicker && (
        <div className={styles.overlay} onClick={() => setShowPicker(false)}>
          <div className={styles.picker} onClick={e => e.stopPropagation()}>
            <div className={styles.pickerHeader}>
              <button
                className={styles.pickerNavBtn}
                onClick={() => setPickerYear(y => y - 1)}
              >&#8592;</button>
              <span className={styles.pickerYear}>{pickerYear}</span>
              <button
                className={styles.pickerNavBtn}
                onClick={() => setPickerYear(y => y + 1)}
              >&#8594;</button>
            </div>
            <div className={styles.monthGrid}>
              {MONTHS_RU.map((name, idx) => {
                const m = idx + 1;
                const isActive = m === month && pickerYear === year;
                return (
                  <button
                    key={m}
                    className={[
                      styles.monthBtn,
                      isActive ? styles.monthBtnActive : '',
                    ].join(' ')}
                    onClick={() => handlePickMonth(m)}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <button className={styles.pickerClose} onClick={() => setShowPicker(false)}>
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plan;
